import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { fetchPublicBlog, mapBlogToCard } from "../../lib/cms";

// Default fallback used when no CMS posts exist yet — matches the legacy
// hardcoded list so the section is never empty.
const FALLBACK_POSTS = [
  {
    id: "10-proven-b2b-digital-marketing-strategies",
    topic: "Strategy",
    date: "Dec 2025",
    title: "10 proven B2B digital marketing strategies that drive real results.",
    img: "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=1200&auto=format&fit=crop",
  },
  {
    id: "ai-search-optimization-b2b",
    topic: "AI Search",
    date: "Dec 2025",
    title: "The ultimate guide to AI search optimization in B2B marketing.",
    img: "https://images.unsplash.com/photo-1620121692029-d088224ddc74?q=80&w=1200&auto=format&fit=crop",
  },
  {
    id: "kaboom-abm-vs-traditional-content",
    topic: "ABM",
    date: "Nov 2025",
    title:
      "KABOOM-ABM vs traditional B2B content platforms — which drives stronger pipeline?",
    img: "https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?q=80&w=1200&auto=format&fit=crop",
  },
];

export default function OurThinking() {
  // Show the featured post first if any, then the next 2 most recent CMS posts.
  // Fall back to the legacy hardcoded list if the CMS has nothing yet.
  const [posts, setPosts] = useState(FALLBACK_POSTS);

  useEffect(() => {
    let cancelled = false;
    fetchPublicBlog()
      .then((data) => {
        if (cancelled || !Array.isArray(data) || data.length === 0) return;
        const cards = data.map(mapBlogToCard);
        const featured = cards.find((p) => p.featured);
        const rest = cards.filter((p) => p !== featured);
        const merged = featured ? [featured, ...rest] : cards;
        // Always render exactly 3 cards. If the CMS has fewer than 3
        // posts (common just after launch), top up with curated fallback
        // posts so the section never looks broken or thin.
        const seen = new Set(merged.map((p) => p.id));
        const padded = [
          ...merged,
          ...FALLBACK_POSTS.filter((p) => !seen.has(p.id)),
        ].slice(0, 3);
        setPosts(padded);
      })
      .catch(() => {
        /* keep fallback */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      id="thinking"
      data-testid="our-thinking"
      className="section-light py-section relative overflow-hidden"
    >
      <div className="bg-orbs bg-orbs-alt" />
      <div className="container-xl relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#86868B] mb-5">
              Our thinking
            </div>
            <h2 className="font-display text-[#1D1D1F] text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02] font-semibold max-w-3xl">
              Insight, ahead{" "}
              <span className="font-serif italic font-light text-[#86868B]">
                of the curve.
              </span>
            </h2>
          </div>
          <a
            href="/thinking"
            data-testid="thinking-view-all"
            className="btn-pill btn-ghost-light text-sm"
          >
            View all
            <ArrowUpRight size={14} strokeWidth={2.2} />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {posts.map((p, i) => (
            <motion.a
              key={p.id || i}
              href={`/thinking/${p.id}`}
              data-testid={`thinking-post-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="group block bg-white rounded-[28px] overflow-hidden border border-black/5 hover:shadow-[0_24px_60px_-24px_rgba(0,0,0,0.18)] transition-all"
            >
              <div className="aspect-[16/11] overflow-hidden bg-[#F5F5F7] relative">
                <img
                  src={p.img}
                  alt={p.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                {p.featured && i === 0 && (
                  <span
                    data-testid="thinking-featured-tag"
                    className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FFB000] text-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] font-semibold shadow-[0_4px_14px_-4px_rgba(0,0,0,0.3)]"
                  >
                    ★ Featured
                  </span>
                )}
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-[#86868B] mb-6">
                  <span>{p.topic || p.tag}</span>
                  <span className="h-px w-6 bg-[#D2D2D7]" />
                  <span>{p.date}</span>
                </div>
                <h3 className="font-display text-[#1D1D1F] text-xl md:text-2xl tracking-tight leading-snug font-medium group-hover:text-[#FFB000] transition-colors">
                  {p.title}
                </h3>
                <div className="mt-8 inline-flex items-center gap-2 text-sm text-[#1D1D1F] font-medium">
                  Read more
                  <ArrowUpRight size={14} strokeWidth={2.2} />
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
